declare const require: any;
import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { LOG_ROCKET_CONFIGURATION, LogRocketConfiguration } from '@uzurv/core';
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration } from '@uzurv/http';
import { BUG_SNAG_CONFIGURATION, BugsnagConfiguration } from 'projects/sdk/packages/core/src/interfaces/bugsnag-configuration';
import { APP_CONFIGURATION, AppConfiguration } from './app/app-configuration';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const version = require('../../../package.json').version;

if (environment.production) {
  enableProdMode();
}

const main = async () => {
  try {
    let vendorName = 'uzurv';

    if (environment.envName === 'deployed') {
      const subdomain = window.location.hostname.split('.')[0];

      if (!subdomain.includes('crm')) {
        vendorName = subdomain;
      }
    }

    const headers = new Headers();
    headers.append('pragma', 'no-cache');
    headers.append('cache-control', 'no-cache');

    const init = {
      method: 'GET',
      headers: headers
    };

    let data = await fetch(`/assets/configurations/config.json`, init);
    const environmentData = await data.json();

    if (environmentData.envName !== 'prod') {
      const index = vendorName.lastIndexOf(environmentData.envName);
      if (index !== -1) {
        vendorName = vendorName.slice(0, index);
      }
    }

    data = await fetch(`/assets/themes/${vendorName}/config.json`, init);
    const themeConfiguration = await data.json();

    const restConfiguration: UzurvRestConfiguration = {
      claraApiUrl: environmentData.restApiUrl,
      dotsApiUrl: environmentData.dotsApiUrl,
      googleMapsApiKey: environmentData.googleMapsApiKey,
      reportingApiUrl: environmentData.reportingApiUrl,
      httpDebugMode: false
    };

    const bugsnagConfiguration: BugsnagConfiguration = {
      bugsnagApiKey: environmentData.bugsnagApiKeyWeb,
      autoCaptureSessions: environmentData.bugsnagAutoCaptureSessions,
      appVersion: environmentData.production ? version : version.split('-')[0],
      notifyReleaseStages: ['dev', 'qa', 'qa2', 'staging', 'training', 'prod'],
      enableRemoteErrorLogging: environmentData.bugsnagEnableRemoteErrorLogging,
      releaseStage: environmentData.envName
    };

    const applicationConfiguration: AppConfiguration = {
      production: environmentData.production,
      version: environmentData.production ? version : version.split('-')[0],
      webApplicationUrl: environmentData.webApplicationUrl,
      googleMapsApiKey: environmentData.googleMapsApiKey,
      listPollingSeconds: environmentData.listPollingSeconds,
      operationsAlertPollingSeconds: environmentData.operationsAlertPollingSeconds,
      authTimeoutMinutes: environmentData.authTimeoutMinutes,
      operationsPhoneNumber: environmentData.operationsPhoneNumber,
      name: themeConfiguration.name,
      shortName: themeConfiguration.shortName,
      logo: themeConfiguration.logo,
      googleAnalyticsMeasurementId: environmentData.googleAnalyticsMeasurementId
    };

    const logRocketConfiguration: LogRocketConfiguration = {
      logRocketProjectKey: environmentData.logRocketProjectKey,
      logRocketSessionCaptureEnabled: environmentData.logRocketSessionCaptureEnabled
    };

    new Promise<void>((resolve, reject) => {
      const link = document.createElement('link');
      link.type = 'text/css';
      link.rel = 'stylesheet';
      link.onload = () => {
        resolve();
      };
      link.href = `${themeConfiguration.theme}?timestamp=${new Date().getTime()}`;

      const head = document.getElementsByTagName('head')[0];
      head.appendChild(link);
    }).then(() => {
      platformBrowserDynamic([
        { provide: UZURV_REST_CONFIGURATION, useValue: restConfiguration },
        { provide: BUG_SNAG_CONFIGURATION, useValue: bugsnagConfiguration },
        { provide: APP_CONFIGURATION, useValue: applicationConfiguration },
        { provide: LOG_ROCKET_CONFIGURATION, useValue: logRocketConfiguration }
      ]).bootstrapModule(AppModule);
    });
  } catch (error) {
    console.error(error);
  }
};

main();
